/**
 * Bottom tab bar for phones. Same destinations as the sidebar, never a second
 * layout.
 *
 * Four tabs and More. More opens a sheet with everything the sidebar has that
 * the bar has no room for: the app's later nav items, then System, Users and
 * Settings (each only if the role may see it, exactly as the sidebar decides),
 * Account and Sign out. Without it, a phone reached those only through the
 * sidebar icon in the corner, which a newcomer never finds.
 */
import { Link, useRouterState } from '@tanstack/react-router'
import { ChevronRight, LogOut, type LucideIcon, Menu, User } from 'lucide-react'
import { useState } from 'react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { can, type Role } from '@/core/access'
import { cn } from '@/lib/utils'
import type { AccountSummary } from './app-sidebar'
import { adminNavItems, isNavItemActive, type NavItem } from './nav-config'

/** Tabs before More. Five slots in all — more than that and labels truncate. */
const TABS_BEFORE_MORE = 4

export interface MobileTabBarProps {
  items: NavItem[]
  counts?: Record<string, number>
  account?: AccountSummary
  onSignOut?: () => void
}

export interface MoreLink {
  id: string
  title: string
  icon: LucideIcon
  url: string
  /** Paths that count as this destination for the active state. */
  isActive: (pathname: string) => boolean
}

/**
 * What More lists, in order: the nav items past the fourth tab, the admin
 * destinations this role may see, then Account. An unknown role (still
 * loading) gets no admin links, so Staff never see a flash of them.
 */
export function moreDestinations(items: readonly NavItem[], role: Role | undefined): MoreLink[] {
  const overflow: MoreLink[] = items.slice(TABS_BEFORE_MORE).map((item) => ({
    id: item.id,
    title: item.title,
    icon: item.icon,
    url: item.url,
    isActive: (p) => isNavItemActive(p, item.url),
  }))
  const admin = adminNavItems.filter((link) => role !== undefined && can(role, link.permission))
  const account: MoreLink = {
    id: 'account',
    title: 'Account',
    icon: User,
    url: '/account',
    isActive: (p) => isNavItemActive(p, '/account'),
  }
  return [...overflow, ...admin, account]
}

const TAB_CLASS =
  'relative flex min-h-14 w-full min-w-0 flex-col items-center justify-center gap-1 px-1 text-xs font-semibold transition-colors'

export function MobileTabBar({ items, counts, account, onSignOut }: MobileTabBarProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [moreOpen, setMoreOpen] = useState(false)

  const moreLinks = moreDestinations(items, account?.role)
  const tabs = items.slice(0, TABS_BEFORE_MORE)
  const moreActive = moreLinks.some((link) => link.isActive(pathname))

  return (
    <nav
      aria-label="Primary"
      className="bg-sidebar/90 fixed inset-x-0 bottom-0 z-20 border-t backdrop-blur"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* One equal column per tab, however many there are. */}
      <ul
        className="grid"
        style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}
      >
        {tabs.map((item) => {
          const active = isNavItemActive(pathname, item.url)
          const count = item.badge ? (counts?.[item.id] ?? 0) : 0
          return (
            <li key={item.id}>
              <Link
                to={item.url}
                aria-current={active ? 'page' : undefined}
                className={cn(TAB_CLASS, active ? 'text-primary' : 'text-muted-foreground')}
              >
                <item.icon className="size-5" strokeWidth={active ? 2.2 : 1.8} aria-hidden="true" />
                <span className="max-w-full truncate">{item.title}</span>
                {count > 0 ? (
                  <span className="bg-primary text-primary-foreground absolute top-1.5 right-[22%] min-w-5 rounded-full px-1 text-xs leading-5">
                    {count}
                  </span>
                ) : null}
              </Link>
            </li>
          )
        })}
        <li>
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-haspopup="dialog"
            aria-current={moreActive ? 'page' : undefined}
            className={cn(TAB_CLASS, moreActive ? 'text-primary' : 'text-muted-foreground')}
          >
            <Menu className="size-5" strokeWidth={moreActive ? 2.2 : 1.8} aria-hidden="true" />
            <span className="max-w-full truncate">More</span>
          </button>
        </li>
      </ul>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="max-h-[85dvh] gap-2 overflow-y-auto rounded-t-xl">
          <SheetHeader className="p-4 pb-0">
            <SheetTitle className="text-lg">More</SheetTitle>
            {account ? (
              <p className="truncate pr-12 text-sm text-muted-foreground">
                {account.name} · {account.email}
              </p>
            ) : null}
          </SheetHeader>
          <ul
            className="flex flex-col px-2 pb-4"
            style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
          >
            {moreLinks.map((link) => {
              const active = link.isActive(pathname)
              return (
                <li key={link.id}>
                  <Link
                    to={link.url}
                    onClick={() => setMoreOpen(false)}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex min-h-12 items-center gap-3 rounded-lg px-3 text-base font-semibold',
                      active ? 'bg-muted text-primary' : 'text-foreground hover:bg-muted',
                    )}
                  >
                    <link.icon className="size-5 shrink-0" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">{link.title}</span>
                    <ChevronRight className="size-4 text-muted-foreground" aria-hidden="true" />
                  </Link>
                </li>
              )
            })}
            {onSignOut ? (
              <li className="mt-2 border-t pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setMoreOpen(false)
                    onSignOut()
                  }}
                  className="flex min-h-12 w-full items-center gap-3 rounded-lg px-3 text-base font-semibold text-foreground hover:bg-muted"
                >
                  <LogOut className="size-5 shrink-0" aria-hidden="true" />
                  Sign out
                </button>
              </li>
            ) : null}
          </ul>
        </SheetContent>
      </Sheet>
    </nav>
  )
}
